import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { useReducedMotion } from '../hooks/useReducedMotion';

export const Hero: React.FC = () => {
  const prefersReducedMotion = useReducedMotion();

  const floatingAnimation = prefersReducedMotion ? {} : {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  const handleGetStarted = () => {
    const downloadSection = document.getElementById('download');
    if (downloadSection) {
      downloadSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLearnMore = () => {
    const featuresSection = document.getElementById('features');
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 sm:pt-24 lg:pt-16">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-100 via-blue-50 to-purple-100 dark:from-orange-900/20 dark:via-blue-900/20 dark:to-purple-900/20">
        <motion.div
          className="absolute inset-0 opacity-30"
          animate={prefersReducedMotion ? {} : {
            background: [
              "radial-gradient(circle at 20% 80%, #FF6B35 0%, transparent 50%)",
              "radial-gradient(circle at 80% 20%, #3B82F6 0%, transparent 50%)",
              "radial-gradient(circle at 40% 40%, #8B5CF6 0%, transparent 50%)",
            ]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[calc(100vh-10rem)] lg:min-h-0">
          {/* Text Content */}
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center lg:text-left order-1 lg:order-1 flex flex-col justify-center"
          >
            <motion.h1 
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 leading-[1.1] sm:leading-tight"
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Connect Better,{' '}
              <span className="bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent block sm:inline">
                Grow Together
              </span>
            </motion.h1>
            
            <motion.p 
              className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 dark:text-gray-300 mb-6 sm:mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed"
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              Analyze, understand, and improve your relationships with AI-powered insights from Gemini 2.5 Flash.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start items-center"
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <motion.button
                onClick={handleGetStarted}
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full font-semibold text-sm sm:text-base lg:text-lg flex items-center justify-center gap-2 shadow-lg min-h-[48px] sm:min-h-[52px]"
                whileHover={prefersReducedMotion ? {} : { 
                  scale: 1.05, 
                  boxShadow: "0 20px 40px rgba(255, 107, 53, 0.3)" 
                }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
              >
                <span className="hidden sm:inline">Get Started Free</span>
                <span className="sm:hidden">Get Started</span>
                <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5" />
              </motion.button>
              
              <motion.button
                onClick={handleLearnMore}
                className="px-6 py-3 sm:py-4 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-200 font-medium text-sm sm:text-base"
                whileHover={prefersReducedMotion ? {} : { y: -2 }}
              >
                Learn More
              </motion.button>
            </motion.div>
          </motion.div>

          {/* Floating Illustration */}
          <motion.div
            className="relative order-2 lg:order-2 flex items-center justify-center"
            initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
          >
            <motion.div
              className="relative w-full max-w-[280px] sm:max-w-sm md:max-w-md mx-auto"
              animate={floatingAnimation}
            >
              {/* Main Card */}
              <div className="bg-white/20 dark:bg-gray-800/20 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl border border-white/30 dark:border-gray-700/30">
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 dark:text-white">
                      Relationship Growth
                    </h3>
                    <div className="w-2.5 sm:w-3 h-2.5 sm:h-3 bg-green-400 rounded-full animate-pulse"></div>
                  </div>
                  
                  {[
                    'Better communication skills',
                    'Increased empathy understanding',
                    'Conflict resolution strategies',
                    'Emotional intelligence boost'
                  ].map((item, index) => (
                    <motion.div
                      key={item}
                      className="flex items-start gap-2 sm:gap-3"
                      initial={prefersReducedMotion ? {} : { opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 1 + index * 0.1 }}
                    >
                      <CheckCircle2 className="w-3.5 sm:w-4 md:w-5 h-3.5 sm:h-4 md:h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs sm:text-sm md:text-base text-gray-700 dark:text-gray-300 leading-relaxed">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Floating Elements */}
              <motion.div
                className="absolute -top-1.5 sm:-top-2 md:-top-4 -right-1.5 sm:-right-2 md:-right-4 w-8 sm:w-12 md:w-16 h-8 sm:h-12 md:h-16 bg-gradient-to-r from-orange-400 to-pink-400 rounded-full opacity-60 blur-sm"
                animate={prefersReducedMotion ? {} : {
                  y: [0, -15, 0],
                  scale: [1, 1.1, 1],
                }}
                transition={{ duration: 4, repeat: Infinity, delay: 1 }}
              />
              
              <motion.div
                className="absolute -bottom-1.5 sm:-bottom-2 md:-bottom-4 -left-1.5 sm:-left-2 md:-left-4 w-6 sm:w-8 md:w-12 h-6 sm:h-8 md:h-12 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full opacity-60 blur-sm"
                animate={prefersReducedMotion ? {} : {
                  y: [0, 12, 0],
                  scale: [1, 0.9, 1],
                }}
                transition={{ duration: 3.5, repeat: Infinity, delay: 0.5 }}
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};